import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../api';

export default function ReglasContables({ clienteId }) {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nueva, setNueva] = useState({ rutProveedor: '', nombreProveedor: '', cuenta: '', cr: '' });
  const [guardando, setGuardando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editValores, setEditValores] = useState({});
  const [busqueda, setBusqueda] = useState('');
  const [importTexto, setImportTexto] = useState('');
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    if (clienteId) cargarReglas();
  }, [clienteId]);

  const cargarReglas = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contabilidad/reglas/${clienteId}`);
      setReglas(data);
    } catch (_) {
      toast.error('Error cargando reglas');
    } finally {
      setLoading(false);
    }
  };

  const agregarRegla = async () => {
    if (!nueva.rutProveedor.trim()) return toast.error('RUT proveedor requerido');
    if (!nueva.cuenta.trim())       return toast.error('Cuenta requerida');
    setGuardando(true);
    try {
      await api.post(`/contabilidad/reglas/${clienteId}`, nueva);
      toast.success('Regla agregada');
      setNueva({ rutProveedor: '', nombreProveedor: '', cuenta: '', cr: '' });
      cargarReglas();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error agregando regla');
    } finally {
      setGuardando(false);
    }
  };

  const iniciarEdicion = (regla) => {
    setEditandoId(regla._id);
    setEditValores({ nombreProveedor: regla.nombreProveedor, cuenta: regla.cuenta, cr: regla.cr || '', activa: regla.activa });
  };

  const guardarEdicion = async (reglaId) => {
    try {
      await api.put(`/contabilidad/reglas/item/${reglaId}`, editValores);
      toast.success('Regla actualizada');
      setEditandoId(null);
      cargarReglas();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error actualizando');
    }
  };

  const eliminarRegla = async (reglaId) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await api.delete(`/contabilidad/reglas/item/${reglaId}`);
      toast.success('Regla eliminada');
      cargarReglas();
    } catch (err) {
      toast.error('Error eliminando');
    }
  };

  const importarReglas = async () => {
    // Formato esperado: líneas con RUT;NOMBRE;CUENTA;CR (o solo RUT;CUENTA)
    const lineas = importTexto.trim().split('\n').filter(l => l.trim());
    const reglasParsed = [];
    const errores = [];

    for (const linea of lineas) {
      const partes = linea.split(';').map(p => p.trim());
      if (partes.length < 2) { errores.push(`Línea inválida: ${linea}`); continue; }

      if (partes.length === 2) {
        reglasParsed.push({ rutProveedor: partes[0], nombreProveedor: '', cuenta: partes[1], cr: '' });
      } else if (partes.length === 3) {
        reglasParsed.push({ rutProveedor: partes[0], nombreProveedor: partes[1], cuenta: partes[2], cr: '' });
      } else {
        reglasParsed.push({ rutProveedor: partes[0], nombreProveedor: partes[1], cuenta: partes[2], cr: partes[3] || '' });
      }
    }

    if (errores.length > 0) return toast.error(`Errores: ${errores.join(', ')}`);
    if (reglasParsed.length === 0) return toast.error('No hay reglas para importar');

    try {
      const { data } = await api.post(`/contabilidad/reglas/${clienteId}/bulk`, { reglas: reglasParsed });
      toast.success(`Importadas: ${data.insertadas} nuevas, ${data.actualizadas} actualizadas`);
      setImportTexto('');
      setShowImport(false);
      cargarReglas();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error importando');
    }
  };

  const reglasFiltradas = reglas.filter(r =>
    !busqueda ||
    r.rutProveedor.toLowerCase().includes(busqueda.toLowerCase()) ||
    r.nombreProveedor?.toLowerCase().includes(busqueda.toLowerCase()) ||
    r.cuenta.includes(busqueda)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{reglas.length} reglas · {reglas.filter(r => r.activa).length} activas</p>
        <button
          onClick={() => setShowImport(p => !p)}
          className="text-sm text-emerald-600 hover:text-emerald-800"
        >
          {showImport ? '✕ Cerrar importación' : '↑ Importar masivo'}
        </button>
      </div>

      {/* Importación masiva */}
      {showImport && (
        <div className="bg-gray-50 border rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-2">
            Una regla por línea: <code className="bg-gray-200 px-1 rounded">RUT;NOMBRE;CUENTA;CR</code>{' '}
            (NOMBRE y CR son opcionales)
          </p>
          <textarea
            value={importTexto}
            onChange={e => setImportTexto(e.target.value)}
            rows={6}
            className="w-full border rounded-lg px-3 py-2 text-xs font-mono resize-none"
            placeholder={"76.123.456-7;Proveedor SA;4110;\n96.789.000-1;Otro Proveedor;4120;"}
          />
          <button
            onClick={importarReglas}
            className="mt-2 bg-emerald-600 text-white text-sm py-1.5 px-3 rounded-lg hover:bg-emerald-700"
          >
            Importar
          </button>
        </div>
      )}

      {/* Agregar nueva regla */}
      <div className="bg-gray-50 border rounded-lg p-3">
        <p className="text-xs font-medium text-gray-500 mb-2">Nueva regla</p>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={nueva.rutProveedor}
            onChange={e => setNueva(p => ({ ...p, rutProveedor: e.target.value }))}
            placeholder="RUT proveedor"
            className="border rounded px-2 py-1.5 text-sm w-36"
          />
          <input
            type="text"
            value={nueva.nombreProveedor}
            onChange={e => setNueva(p => ({ ...p, nombreProveedor: e.target.value }))}
            placeholder="Nombre (opcional)"
            className="border rounded px-2 py-1.5 text-sm flex-1 min-w-32"
          />
          <input
            type="text"
            value={nueva.cuenta}
            onChange={e => setNueva(p => ({ ...p, cuenta: e.target.value }))}
            placeholder="Cuenta"
            className="border rounded px-2 py-1.5 text-sm w-24"
          />
          <input
            type="text"
            value={nueva.cr}
            onChange={e => setNueva(p => ({ ...p, cr: e.target.value }))}
            placeholder="CR (opcional)"
            className="border rounded px-2 py-1.5 text-sm w-24"
          />
          <button
            onClick={agregarRegla}
            disabled={guardando}
            className="bg-emerald-600 text-white text-sm px-3 py-1.5 rounded hover:bg-emerald-700 disabled:opacity-50"
          >
            + Agregar
          </button>
        </div>
      </div>

      {/* Búsqueda */}
      <input
        type="text"
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        placeholder="Buscar por RUT, nombre o cuenta..."
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />

      {/* Tabla de reglas */}
      {loading ? (
        <p className="text-sm text-gray-400">Cargando reglas...</p>
      ) : reglasFiltradas.length === 0 ? (
        <p className="text-sm text-gray-400">
          {reglas.length === 0 ? 'No hay reglas configuradas para esta empresa.' : 'Sin resultados.'}
        </p>
      ) : (
        <div className="overflow-auto max-h-96 border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">RUT Proveedor</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Nombre</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Cuenta</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">CR</th>
                <th className="px-3 py-2 text-center font-medium text-gray-500">Activa</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {reglasFiltradas.map(r => (
                <tr key={r._id} className={`border-t ${!r.activa ? 'opacity-40' : ''}`}>
                  <td className="px-3 py-1.5 font-mono">{r.rutProveedor}</td>
                  {editandoId === r._id ? (
                    <>
                      <td className="px-2 py-1">
                        <input
                          value={editValores.nombreProveedor}
                          onChange={e => setEditValores(p => ({ ...p, nombreProveedor: e.target.value }))}
                          className="border rounded px-1.5 py-1 text-xs w-full"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={editValores.cuenta}
                          onChange={e => setEditValores(p => ({ ...p, cuenta: e.target.value }))}
                          className="border rounded px-1.5 py-1 text-xs w-20"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={editValores.cr}
                          onChange={e => setEditValores(p => ({ ...p, cr: e.target.value }))}
                          className="border rounded px-1.5 py-1 text-xs w-20"
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <input
                          type="checkbox"
                          checked={editValores.activa}
                          onChange={e => setEditValores(p => ({ ...p, activa: e.target.checked }))}
                        />
                      </td>
                      <td className="px-2 py-1 flex gap-1">
                        <button onClick={() => guardarEdicion(r._id)} className="text-emerald-600 hover:text-emerald-800 font-bold">✓</button>
                        <button onClick={() => setEditandoId(null)} className="text-gray-400 hover:text-gray-600">✕</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-1.5 text-gray-500">{r.nombreProveedor || '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-blue-700">{r.cuenta}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.cr || '—'}</td>
                      <td className="px-3 py-1.5 text-center">{r.activa ? '✓' : '—'}</td>
                      <td className="px-3 py-1.5 flex gap-2">
                        <button onClick={() => iniciarEdicion(r)} className="text-blue-500 hover:text-blue-700">✏</button>
                        <button onClick={() => eliminarRegla(r._id)} className="text-red-400 hover:text-red-600">🗑</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
